import React, { Component } from "react";
import * as PropTypes from "prop-types";
import { Button, Col, Form, message, Modal, Row, Switch } from "antd";

import moment from "moment";

import { logService } from "./../../redux/services";

import { UIDrawerForm, UIFormSection } from "./../../components";

const METHOD_TONES = {
	GET   : "byp-method--get",
	POST  : "byp-method--post",
	PUT   : "byp-method--put",
	PATCH : "byp-method--put",
	DELETE: "byp-method--delete",
};

const MAX_DIFF_LINES = 2000;

const timezone = () => {
	try
	{
		return Intl.DateTimeFormat().resolvedOptions().timeZone;
	}
	catch( e )
	{
		return "";
	}
};

/**
 * @param {*} data
 *
 * @returns {string}
 */
const toJson = (data) => (data === null || data === undefined) ? "" : JSON.stringify(data, null, 2);

/**
 * @param {string} oldText
 * @param {string} newText
 *
 * @returns {Array}
 */
const buildDiff = (oldText, newText) => {
	const a = oldText ? oldText.split("\n") : [];
	const b = newText ? newText.split("\n") : [];

	if( a.length > MAX_DIFF_LINES || b.length > MAX_DIFF_LINES )
	{
		return [
			...a.map((content, index) => ({type: "removed", content, number: index + 1})),
			...b.map((content, index) => ({type: "added", content, number: index + 1})),
		];
	}

	const lcs = Array.from({length: a.length + 1}, () => new Array(b.length + 1).fill(0));

	for( let i = a.length - 1; i >= 0; i-- )
	{
		for( let j = b.length - 1; j >= 0; j-- )
		{
			lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
		}
	}

	const lines = [];

	let i = 0;
	let j = 0;

	while( i < a.length && j < b.length )
	{
		if( a[i] === b[j] )
		{
			lines.push({type: "equal", content: a[i], number: j + 1});
			i++;
			j++;
		}
		else if( lcs[i + 1][j] >= lcs[i][j + 1] )
		{
			lines.push({type: "removed", content: a[i], number: i + 1});
			i++;
		}
		else
		{
			lines.push({type: "added", content: b[j], number: j + 1});
			j++;
		}
	}

	while( i < a.length )
	{
		lines.push({type: "removed", content: a[i], number: i + 1});
		i++;
	}

	while( j < b.length )
	{
		lines.push({type: "added", content: b[j], number: j + 1});
		j++;
	}

	return lines;
};

/**
 * @param {Array} lines
 *
 * @returns {Object}
 */
const diffStats = (lines) => {
	let added   = 0;
	let removed = 0;
	let changed = 0;

	let blockAdded   = 0;
	let blockRemoved = 0;

	const closeBlock = () => {
		const pairs = Math.min(blockAdded, blockRemoved);

		changed += pairs;
		added   += blockAdded - pairs;
		removed += blockRemoved - pairs;

		blockAdded   = 0;
		blockRemoved = 0;
	};

	lines.forEach(line => {
		if( line.type === "added" )
		{
			blockAdded++;
		}
		else if( line.type === "removed" )
		{
			blockRemoved++;
		}
		else
		{
			closeBlock();
		}
	});

	closeBlock();

	return {
		added,
		removed,
		changed,
		affected: added + removed + changed,
	};
};

class Show extends Component {
	static propTypes = {
		visible : PropTypes.bool.isRequired,
		onClose : PropTypes.func.isRequired,
		external: PropTypes.bool,
	};

	constructor(props) {
		super(props);

		this.state = {
			isLoading      : true,
			uuid           : 0,
			item           : {},
			showLineNumbers: true,
		};
	}

	onOpen = (uuid) => {
		this.setState({
			isLoading: true,
			uuid     : uuid,
			item     : {},
		});

		logService.show({uuid})
		.then((response) => {
			this.setState({
				isLoading: false,
				item     : response.data.data,
			});
		})
		.catch((data) => {
			Modal.error({
				title  : "Ocorreu um erro!",
				content: String(data),
				onOk   : () => {
					// Force close
					return this.onClose();
				}
			});
		});
	};

	resetFields = () => {
		this.setState({
			item           : {},
			showLineNumbers: true,
		});
	};

	onClose = () => {
		// Reset fields
		this.resetFields();

		// Callback
		this.props.onClose();
	};

	get payload() {
		const {item} = this.state;

		return JSON.stringify({
			old_data: item.old_data ?? null,
			new_data: item.new_data ?? null,
		}, null, 2);
	}

	onCopy = () => {
		const copy = navigator.clipboard
			? navigator.clipboard.writeText(this.payload)
			: Promise.reject();

		copy
		.then(() => message.success("JSON copiado para a área de transferência."))
		.catch(() => message.error("Não foi possível copiar o JSON."));
	};

	onDownload = () => {
		const url  = window.URL.createObjectURL(new Blob([this.payload], {type: "application/json"}));
		const link = document.createElement("a");

		link.href     = url;
		link.download = `alteracao-${this.state.uuid}.json`;

		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);

		window.URL.revokeObjectURL(url);
	};

	renderBadgeValue = (id, name) => {
		if( !id ) return "-";

		return (
			<span className="byp-log-badge-value">
				<span className="byp-log-badge">{id}</span>{name || "-"}
			</span>
		)
	};

	renderStat = (tone, icon, label, value) => (
		<div className={`byp-log-stat byp-log-stat--${tone}`}>
			<div className="byp-log-stat__head">
				<span className="byp-log-stat__icon">{icon}</span>
				<span className="byp-log-stat__label">{label}</span>
			</div>
			<span className="byp-log-stat__value">{value}</span>
		</div>
	);

	renderDiff = (lines) => {
		const {showLineNumbers} = this.state;

		if( !lines.length )
		{
			return <p className="byp-diff-empty">Este registro não guardou dados de antes e depois.</p>
		}

		return (
			<div className="byp-diff">
				<div className="byp-diff__toolbar">
					<span className="byp-diff__caption">Exibindo diferenças</span>
					<label className="byp-diff__toggle">
						<Switch
							size="small"
							checked={showLineNumbers}
							onChange={(checked) => this.setState({showLineNumbers: checked})}
						/>
						Mostrar números de linha
					</label>
				</div>
				<div className="byp-diff__body">
					{lines.map((line, index) => (
						<div key={index} className={`byp-diff__line byp-diff__line--${line.type}`}>
							{showLineNumbers && <span className="byp-diff__number">{line.number}</span>}
							<span className="byp-diff__marker">{line.type === "added" ? "+" : (line.type === "removed" ? "−" : "")}</span>
							<code className="byp-diff__content">{line.content}</code>
						</div>
					))}
				</div>
			</div>
		)
	};

	render() {
		const {visible}         = this.props;
		const {isLoading, item} = this.state;

		const lines = isLoading ? [] : buildDiff(toJson(item.old_data), toJson(item.new_data));
		const stats = diffStats(lines);

		return (
			<UIDrawerForm
				visible={visible}
				width={1100}
				onClose={this.onClose}
				isLoading={isLoading}
				showBtnSave={false}
				icon={<i className="fal fa-history" />}
				title="Visualizar alteração"
				subtitle="Confira os dados do registro e o que mudou no item."
				drawerProps={{className: "drawer-form drawer-log-view"}}>
				<Form layout="vertical">
					<UIFormSection
						icon={<i className="fal fa-clipboard-list" />}
						title="Detalhes da alteração">
						<Row gutter={16}>
							<Col xs={24}>
								<Form.Item label="Mensagem">
									{item.message ?? "-"}
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col xs={24} md={12}>
								<Form.Item label="Usuário">
									{this.renderBadgeValue(item.user_id, item.user?.name)}
								</Form.Item>
							</Col>
							<Col xs={24} md={12}>
								<Form.Item label="Item">
									{this.renderBadgeValue(item.log_id, item.log_name)}
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col xs={24} md={12}>
								<Form.Item label="IP">
									{item.ip ?? "-"}
								</Form.Item>
							</Col>
							<Col xs={24} md={12}>
								<Form.Item label="Data">
									{item.created_at ? (
										<span className="byp-log-view-date">
											{moment(item.created_at).format("DD/MM/YYYY [às] HH:mm:ss")}
											<small>({timezone()})</small>
										</span>
									) : "-"}
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col xs={24}>
								<Form.Item label="User agent">
									{item.user_agent ?? "-"}
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={16}>
							<Col xs={24}>
								<Form.Item label="URL">
									{item.url ? (
										<span className="byp-log-url">
											<span className={`byp-method ${METHOD_TONES[item.method] || "byp-method--get"}`}>{item.method}</span>
											<code>{item.url}</code>
										</span>
									) : "-"}
								</Form.Item>
							</Col>
						</Row>
					</UIFormSection>
					<UIFormSection
						icon={<i className="fal fa-code" />}
						title="Diferenças"
						description="Comparação entre os dados antes e depois da alteração."
						extra={<span className="byp-log-format">Formato: JSON</span>}>
						<div className="byp-log-stats">
							{this.renderStat("success", <i className="fal fa-plus" />, "Adições", stats.added)}
							{this.renderStat("error", <i className="fal fa-minus" />, "Remoções", stats.removed)}
							{this.renderStat("primary", <i className="fal fa-pen" />, "Alterações", stats.changed)}
							{this.renderStat("neutral", <i className="fal fa-list" />, "Linhas afetadas", stats.affected)}
						</div>
						{this.renderDiff(lines)}
						{lines.length > 0 && <div className="byp-log-diff-foot">
							<span className="byp-diff__legend">
								Legenda:
								<span className="byp-diff__legend-tag byp-diff__legend-tag--removed">− Removido</span>
								<span className="byp-diff__legend-tag byp-diff__legend-tag--added">+ Adicionado</span>
							</span>
							<span className="byp-log-diff-foot__actions">
								<Button className="action-btn action-btn-secondary" icon={<i className="fal fa-copy" />} onClick={this.onCopy}>Copiar</Button>
								<Button className="action-btn action-btn-secondary" icon={<i className="fal fa-download" />} onClick={this.onDownload}>Baixar JSON</Button>
							</span>
						</div>}
					</UIFormSection>
				</Form>
			</UIDrawerForm>
		)
	}
}

export default Show;
